import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Loader2, Receipt } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import BillingDetailsSection from '@/components/billing/BillingDetailsSection';
import { useTitleBar } from '@/context/TitleBarProvider';
import { downloadInvoicePdf, fetchInvoices, fetchTrialStatus } from '@/data/billing';
import type { Invoice } from '@/types/billing';


const STATUS_STYLE: Record<string, string> = {
  paid: 'bg-green-500/10 text-green-700 border-green-500/30',
  pending: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  overdue: 'bg-red-500/10 text-red-700 border-red-500/30',
};

function vatSummary(invoice: Invoice) {
  const rate = Number(invoice.tax_rate_percent);
  if (rate > 0) return `${invoice.subtotal} + VAT ${rate}% (${invoice.tax} ${invoice.currency})`;
  switch (invoice.tax_treatment) {
    case 'reverse_charge': return 'No VAT: reverse charge, you account for it';
    case 'outside_eu': return 'No VAT: outside the scope of EU VAT';
    case 'not_registered': return 'No VAT: supplier not registered';
    default: return 'No VAT';
  }
}

function InvoiceList({ invoices }: { invoices: Invoice[] }) {
  const [downloading, setDownloading] = React.useState<string | null>(null);

  const download = async (invoice: Invoice) => {
    setDownloading(invoice.id);
    try {
      await downloadInvoicePdf(invoice);
    } catch {
      toast.error('Could not download that invoice.');
    } finally {
      setDownloading(null);
    }
  };

  if (invoices.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center" data-testid="no-invoices">
        No invoices yet. Your first one arrives after the month your trial ends.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="invoice-list">
      {invoices.map((invoice) => (
        <div
          key={invoice.id}
          className="flex flex-wrap items-center gap-3 rounded-md border p-3"
          data-testid={`invoice-${invoice.invoice_number}`}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{invoice.invoice_number}</span>
              <Badge variant="outline" className={STATUS_STYLE[invoice.status] ?? ''}>
                {invoice.status}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {invoice.billing_period} · due{' '}
              {new Date(invoice.due_date).toLocaleDateString()}
            </p>
            <ul className="mt-1 text-xs text-muted-foreground">
              {invoice.line_items.map((line, index) => (
                <li key={index}>{line.description}</li>
              ))}
            </ul>
          </div>
          <div className="text-right">
            <span className="font-semibold" data-testid={`invoice-total-${invoice.invoice_number}`}>
              {invoice.total} {invoice.currency}
            </span>
            <p className="text-xs text-muted-foreground" data-testid={`invoice-vat-${invoice.invoice_number}`}>
              {vatSummary(invoice)}
            </p>
          </div>
          {invoice.has_pdf && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => download(invoice)}
              disabled={downloading === invoice.id}
              data-testid={`invoice-pdf-${invoice.invoice_number}`}
            >
              {downloading === invoice.id ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Download className="h-3 w-3 mr-1" />
              )}
              PDF
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

export default function BillingPage() {
  const { setTitle, setIcon, setButtons } = useTitleBar();
  React.useEffect(() => {
    setTitle('Billing');
    setIcon(<Receipt size={24} />);
    setButtons(null);
  }, [setTitle, setIcon, setButtons]);

  const invoices = useQuery({ queryKey: ['invoices'], queryFn: fetchInvoices });
  const trial = useQuery({ queryKey: ['trial-status'], queryFn: () => fetchTrialStatus() });

  return (
    <div className="space-y-6" data-testid="billing-page">
      {trial.data?.on_trial && (
        <Card>
          <CardHeader>
            <CardTitle>Free trial</CardTitle>
            <CardDescription data-testid="billing-trial-summary">
              {trial.data.trial_state === 'expired'
                ? 'Your trial has ended. Existing servers keep running; new ones need a paid plan.'
                : `${trial.data.days_left} days left, ${trial.data.server_quota} server included.`}
              {trial.data.advisor_name ? ` Your advisor is ${trial.data.advisor_name}.` : ''}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
          <CardDescription>Unpaid invoices first, then the newest.</CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <InvoiceList invoices={invoices.data ?? []} />
          )}
        </CardContent>
      </Card>

      <BillingDetailsSection />
    </div>
  );
}
